import type { AchievementIcon } from './achievements';
import type { ModeView } from './multiplayer';

/**
 * Versions and publishing (M23, PRD 6.5): the developer publishes an approved version now or at
 * a set time; admins roll back, suspend and reinstate.
 */

/** What became of an approved version once it was up for publishing. Null until then. */
export const RELEASE_STATES = ['live', 'replaced', 'rolled_back', 'skipped'] as const;
export type ReleaseState = (typeof RELEASE_STATES)[number];

export const RELEASE_STATE_NAMES: Record<ReleaseState, string> = {
  live: 'Live',
  replaced: 'Replaced',
  rolled_back: 'Rolled back',
  skipped: 'Skipped',
};

export const PUBLISH_LIMITS = {
  /** A set time is at least this far ahead… */
  leadMinutes: 15,
  /** …and at most this far. */
  aheadDays: 30,
  /** An admin’s reason for a roll back, and message with a suspension. */
  reason: 1000,
  message: 2000,
} as const;

/** Why an admin suspends a game. */
export const SUSPEND_REASONS = {
  rules: 'Breaks the content rules',
  unsafe: 'Broken or unsafe',
  copied: 'Copied or stolen content',
  legal: 'Legal request',
  other: 'Other',
} as const;
export type SuspendReason = keyof typeof SUSPEND_REASONS;
export const SUSPEND_REASON_IDS = Object.keys(SUSPEND_REASONS) as [
  SuspendReason,
  ...SuspendReason[],
];

/** A leaderboard in full, as sent with a version: what live gets when it’s published. */
export interface LiveLeaderboard {
  id: string;
  name: string;
  sort: 'asc' | 'desc';
  format: 'number' | 'time';
  reset: 'none' | 'daily' | 'weekly' | 'monthly';
  min: number | null;
  max: number | null;
}

/** An achievement in full, as sent with a version. */
export interface LiveAchievement {
  id: string;
  name: string;
  description: string;
  points: number;
  hidden: boolean;
  steps: number | null;
  icon: AchievementIcon;
}

export interface LiveSetup {
  leaderboards: LiveLeaderboard[];
  achievements: LiveAchievement[];
  /** Multiplayer modes (M31); missing from versions sent before them. */
  modes?: ModeView[];
}
